import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, OrderStatusBadge, ORDER_STATUS_OPTIONS } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import OrderDetailDialog from "@/components/OrderDetailDialog";
import { AddressFields, addrLine, emptyAddr } from "@/components/AddressFields";
import { parseDiscountExpr, effectivePct } from "@/lib/sales";
import { NotifyResellersButton } from "@/components/NotifyResellersButton";
import { ResellerAlertsHistory } from "@/components/ResellerAlertsHistory";
import { B2BStatsTab } from "@/components/B2BStatsTab";
import { ContactMessagesTab } from "@/components/ContactMessagesTab";
import { Activity, MessageSquare } from "lucide-react";
import {
  ShoppingCart, Users, CalendarDays, Eye, Loader2, Building2, Pencil, Plus, Trash2, Star, Save, Truck, Clock, Mail, Phone, MapPin, Percent, AlertTriangle, BarChart3, CheckCircle2, Globe, ShieldCheck,
} from "lucide-react";

const eur = (n) => {
  const num = Number(n || 0);
  const [i, d] = Math.abs(num).toFixed(2).split(".");
  return `${num < 0 ? "-" : ""}${i.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${d} €`;
};
const dmy = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return "—"; } };
const dmyhm = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
const cartState = (iso) => {
  if (!iso) return null;
  const ageH = (Date.now() - new Date(iso).getTime()) / 36e5;
  return ageH > 24
    ? { label: "Abbandonato", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-300" }
    : { label: "In sospeso", cls: "bg-sky-500/15 text-sky-600 dark:text-sky-300" };
};

// Avviso anti-buchi: rileva fasce non contigue / sovrapposte / che non partono da 0 (solo warning, non blocca).
function validateTiers(tiers) {
  const warns = [];
  const sorted = [...(tiers || [])].map((t) => ({
    min: Number(t.min) || 0,
    max: (t.max === null || t.max === "" || t.max === undefined) ? null : Number(t.max),
  })).sort((a, b) => a.min - b.min);
  if (sorted.length === 0) return warns;
  if (Math.round(sorted[0].min * 100) > 0) {
    warns.push(`La prima fascia parte da ${eur(sorted[0].min)} anziché da 0 €: gli ordini sotto questa soglia resterebbero senza sconto.`);
  }
  for (let i = 0; i < sorted.length; i++) {
    const t = sorted[i];
    if (t.max !== null && t.max <= t.min) {
      warns.push(`Fascia da ${eur(t.min)}: il valore «A» (${eur(t.max)}) deve essere maggiore di «Da».`);
    }
    const next = sorted[i + 1];
    if (!next) continue;
    if (t.max === null) {
      warns.push(`La fascia da ${eur(t.min)} è impostata come «oltre» (senza limite) ma non è l'ultima: le fasce successive non verrebbero applicate.`);
      continue;
    }
    const expected = Math.round((t.max + 0.01) * 100) / 100;
    if (next.min > expected + 0.001) {
      warns.push(`Buco tra ${eur(t.max)} e ${eur(next.min)}: gli importi in questo intervallo non hanno una fascia dedicata.`);
    } else if (next.min <= t.max) {
      warns.push(`Sovrapposizione tra la fascia fino a ${eur(t.max)} e quella che parte da ${eur(next.min)}.`);
    }
  }
  return warns;
}


/* ---------------- Admin Orders ---------------- */
function AdminOrdersTab() {
  const fmt = (d) => d.toISOString().slice(0, 10);
  const today = new Date();
  const yearAgo = new Date(); yearAgo.setFullYear(today.getFullYear() - 1);
  const [from, setFrom] = useState(fmt(yearAgo));
  const [to, setTo] = useState(fmt(today));
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [delOpen, setDelOpen] = useState(false);

  const load = (f = from, t = to) => {
    setLoading(true);
    api.get(`/orders?from_date=${f}&to_date=${t}`)
      .then(({ data }) => { setOrders(data.orders); setSel(new Set()); })
      .catch(() => toast.error("Impossibile caricare gli ordini"))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/orders/${id}`); setDetail(data.order); setDetailOpen(true); }
    catch { toast.error("Impossibile caricare il dettaglio"); }
  };

  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSel((s) => (s.size === orders.length ? new Set() : new Set(orders.map((o) => o._id))));

  const deleteSelected = async () => {
    const ids = [...sel];
    if (!ids.length) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/orders/delete-selected", { ids });
      toast.success(`${data.deleted} ordini eliminati`);
      setOrders((prev) => prev.filter((o) => !sel.has(o._id)));
      if (detail && ids.includes(detail._id)) { setDetailOpen(false); setDetail(null); }
      setSel(new Set());
      setDelOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Eliminazione fallita"); }
    finally { setDeleting(false); }
  };

  const deleteOne = async (id) => {
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Ordine eliminato");
      setOrders((prev) => prev.filter((o) => o._id !== id));
      setSel((s) => { const n = new Set(s); n.delete(id); return n; });
      setDetailOpen(false); setDetail(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Eliminazione fallita"); }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      setOrders((prev) => prev.map((o) => (o._id === id ? { ...o, status } : o)));
      toast.success("Stato aggiornato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Aggiornamento fallito"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-brema-card p-4">
        <div className="flex items-center gap-2 text-foreground"><CalendarDays className="h-4 w-4 text-brand" /><span className="text-sm font-medium">Periodo</span></div>
        <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Da</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="admin-orders-from" className="h-9 w-[160px] bg-brema-bg" /></div>
        <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">A</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="admin-orders-to" className="h-9 w-[160px] bg-brema-bg" /></div>
        <Button onClick={() => load()} disabled={loading} data-testid="admin-orders-apply"
          className="h-9 rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Applica"}
        </Button>
        <div className="ml-auto flex items-center gap-3">
          {sel.size > 0 && (
            <Button size="sm" variant="danger" data-testid="orders-delete-selected-btn" onClick={() => setDelOpen(true)}
              className="h-9 rounded-full text-xs">
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Elimina selezionati ({sel.size})
            </Button>
          )}
          <span className="text-xs text-muted-foreground">{orders.length} ordini</span>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-12 text-center text-muted-foreground">Nessun ordine nel periodo.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-brema-card">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="border-b border-border bg-brema-bg text-left text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="w-10 p-3.5">
                  <Checkbox checked={orders.length > 0 && sel.size === orders.length} onCheckedChange={toggleAll} data-testid="orders-select-all" />
                </th>
                <th className="p-3.5">Data</th>
                <th className="p-3.5">ID</th>
                <th className="p-3.5">Rivenditore</th>
                <th className="p-3.5">Destinatario / Città</th>
                <th className="p-3.5">Stato</th>
                <th className="p-3.5 text-right">Totale</th>
                <th className="p-3.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {orders.map((o) => (
                <tr key={o._id} data-testid="admin-order-row" className="text-foreground">
                  <td className="p-3.5">
                    <Checkbox checked={sel.has(o._id)} onCheckedChange={() => toggleSel(o._id)} data-testid="order-row-checkbox" />
                  </td>
                  <td className="p-3.5 text-foreground">{dmy(o.created_at)}</td>
                  <td className="p-3.5 font-mono text-xs text-muted-foreground">#{o._id.slice(-6).toUpperCase()}</td>
                  <td className="p-3.5">{o.reseller_name || "—"}</td>
                  <td className="p-3.5">
                    <p className="font-medium text-foreground">{o.recipient || "—"}</p>
                    <p className="text-xs text-muted-foreground">{o.ship_city || o.shipping_address?.city || ""}</p>
                  </td>
                  <td className="p-3.5">
                    <Select value={o.status || "in_elaborazione"} onValueChange={(v) => changeStatus(o._id, v)}>
                      <SelectTrigger data-testid="order-status-select" className="h-8 w-[160px] bg-brema-bg text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ORDER_STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-3.5 text-right font-semibold text-foreground">{eur(o.total_incl_vat ?? o.total)}</td>
                  <td className="p-3.5 text-right">
                    <Button size="sm" variant="secondary" data-testid="admin-view-order-btn" onClick={() => openDetail(o._id)}
                      className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover"><Eye className="mr-1.5 h-3.5 w-3.5" /> Dettaglio</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <OrderDetailDialog order={detail} open={detailOpen} onOpenChange={setDetailOpen} onDelete={deleteOne} />

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="max-w-md border-border bg-brema-card" data-testid="orders-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Elimina ordini</DialogTitle>
            <DialogDescription>
              Vuoi eliminare <strong className="text-foreground">{sel.size}</strong> {sel.size === 1 ? "ordine" : "ordini"} dall'archivio? L'azione è irreversibile.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDelOpen(false)}
              className="rounded-full border border-border bg-transparent text-foreground hover:bg-brema-hover">Annulla</Button>
            <Button data-testid="orders-delete-confirm-btn" disabled={deleting} onClick={deleteSelected}
              className="rounded-full bg-brema-red font-semibold text-white hover:bg-brema-red/90">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-1.5 h-4 w-4" /> Elimina</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Admin Resellers ---------------- */
const emptyFiscal = {
  name: "", shop_name: "", company_name: "", vat_number: "", phone: "", contact_email: "", city: "", reseller_tier: "potenziale",
};

function ResellersTab() {
  const [resellers, setResellers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fiscal, setFiscal] = useState(emptyFiscal);
  const [billing, setBilling] = useState({ ...emptyAddr });
  const [addrs, setAddrs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [addrDraft, setAddrDraft] = useState(null); // index or "new" being edited inline
  const [activityOpen, setActivityOpen] = useState(false);
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => api.get("/resellers").then(({ data }) => setResellers(data.resellers)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openActivity = async (r) => {
    setActivityOpen(true); setActivity(null); setActivityLoading(true);
    try { const { data } = await api.get(`/resellers/${r.id}/activity`); setActivity(data); }
    catch { toast.error("Impossibile caricare l'attività"); }
    finally { setActivityLoading(false); }
  };

  const openEdit = (r) => {
    setEditing(r);
    setFiscal({
      name: r.name || "", shop_name: r.shop_name || "", company_name: r.company_name || "",
      vat_number: r.vat_number || "", phone: r.phone || "", contact_email: r.contact_email || "", city: r.city || "",
      reseller_tier: r.reseller_tier || "potenziale",
    });
    setBilling({ ...emptyAddr, ...(r.billing_address || {}) });
    setAddrs(r.shipping_addresses || []);
    setAddrDraft(null);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/resellers/${editing.id}`, {
        ...fiscal, billing_address: billing, shipping_addresses: addrs,
      });
      toast.success("Rivenditore aggiornato");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio fallito"); }
    finally { setSaving(false); }
  };

  const setF = (k) => (e) => setFiscal((f) => ({ ...f, [k]: e.target.value }));
  const addAddr = () => setAddrs((prev) => [...prev, { ...emptyAddr, is_default: prev.length === 0 }]);
  const confirmDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/resellers/${delTarget.id}`);
      toast.success("Rivenditore eliminato");
      setDelTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Eliminazione fallita"); }
    finally { setDeleting(false); }
  };
  const updAddr = (i, v) => setAddrs((prev) => prev.map((a, idx) => (idx === i ? v : a)));
  const delAddr = (i) => setAddrs((prev) => prev.filter((_, idx) => idx !== i));
  const defAddr = (i) => setAddrs((prev) => prev.map((a, idx) => ({ ...a, is_default: idx === i })));

  const officialResellers = resellers.filter((r) => (r.reseller_tier || "potenziale") === "ufficiale");
  return (
    <div className="space-y-4">
      {officialResellers.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-12 text-center text-muted-foreground" data-testid="resellers-empty">Nessun rivenditore ufficiale.</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {officialResellers.map((r) => {
            const cs = r.cart_summary;
            const st = cs ? cartState(cs.updated_at) : null;
            return (
            <div key={r.id} data-testid="reseller-card" className="flex flex-col rounded-xl border border-border bg-brema-card p-4">
              <div className="mb-1 flex items-center gap-2 text-foreground"><Building2 className="h-4 w-4 text-brand" /><span className="font-medium">{r.company_name || r.shop_name || r.name}</span></div>
              <p className="text-xs text-muted-foreground">{r.vat_number || "P.IVA non impostata"}</p>
              <p className="mt-1 text-xs text-muted-foreground">{r.email}</p>
              <div className="mt-2">
                {r.reseller_tier === "ufficiale" ? (
                  <span data-testid="reseller-tier-badge" className="inline-flex items-center gap-1 rounded-full border border-brema-red/40 bg-brema-red/10 px-2 py-0.5 text-[10px] font-semibold text-brema-red">Rivenditore Ufficiale</span>
                ) : (
                  <span data-testid="reseller-tier-badge" className="inline-flex items-center gap-1 rounded-full border border-positive/40 bg-positive/10 px-2 py-0.5 text-[10px] font-semibold text-positive">Potenziale</span>
                )}
              </div>
              <div className="mt-2 space-y-1 border-t border-border pt-2 text-xs">
                <p className="flex items-center gap-1.5 text-muted-foreground" data-testid="reseller-last-login">
                  <Clock className="h-3 w-3" /> Ultimo accesso: <span className="text-foreground">{r.last_login ? dmyhm(r.last_login) : "mai"}</span>
                </p>
                <p className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
                  <ShoppingCart className="h-3 w-3" /> Ordini: <span className="text-foreground">{r.orders_count || 0}</span>
                  {r.last_order_at && <span className="text-muted-foreground">· ultimo {dmy(r.last_order_at)}</span>}
                </p>
                {cs && st && (
                  <p className="flex flex-wrap items-center gap-1.5" data-testid="reseller-cart-badge">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${st.cls}`}>Carrello {st.label}</span>
                    <span className="text-muted-foreground">{cs.units} pz · {eur(cs.total_excl)} <span className="text-[10px]">IVA escl.</span></span>
                  </p>
                )}
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="secondary" data-testid="reseller-activity-btn" onClick={() => openActivity(r)}
                  className="flex-1 rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover"><Eye className="mr-1.5 h-3.5 w-3.5" /> Attività</Button>
                <Button size="sm" variant="secondary" data-testid="edit-reseller-btn" onClick={() => openEdit(r)}
                  className="flex-1 rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover"><Pencil className="mr-1.5 h-3.5 w-3.5" /> Anagrafica</Button>
                <Button size="sm" variant="danger" data-testid="delete-reseller-btn" onClick={() => setDelTarget(r)}
                  className="rounded-full px-2.5 text-xs"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="max-w-md border-border bg-brema-card" data-testid="delete-reseller-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Elimina rivenditore</DialogTitle>
            <DialogDescription>
              Stai per eliminare <span className="font-semibold text-foreground">{delTarget?.company_name || delTarget?.shop_name || delTarget?.name}</span>.
              {(delTarget?.orders_count || 0) > 0
                ? ` Ha ${delTarget.orders_count} ordini: lo storico ordini resterà consultabile, ma il profilo verrà rimosso.`
                : " Il profilo verrà rimosso definitivamente."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDelTarget(null)} data-testid="delete-reseller-cancel"
              className="rounded-full border border-border bg-transparent text-foreground hover:bg-brema-hover">Annulla</Button>
            <Button onClick={confirmDelete} disabled={deleting} data-testid="delete-reseller-confirm"
              className="rounded-full bg-brema-red font-semibold text-white hover:bg-brema-red/90">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Elimina definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto border-border bg-brema-card">
          <DialogHeader>
            <DialogTitle className="text-foreground">Anagrafica rivenditore</DialogTitle>
            <DialogDescription>Dati fiscali primari e indirizzi (controllo Brema).</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="rounded-lg border border-brand/25 bg-brand/5 p-3">
              <Label className="text-xs font-semibold text-foreground">Livello account B2B</Label>
              <p className="mb-2 mt-0.5 text-[11px] text-muted-foreground">"Ufficiale" sblocca prezzi B2B, giacenze live, carrello e ordini. Imposta dopo aver verificato l'esclusiva di zona e inserito i dati fiscali.</p>
              <Select value={fiscal.reseller_tier} onValueChange={(v) => setFiscal((f) => ({ ...f, reseller_tier: v }))}>
                <SelectTrigger data-testid="rf-tier" className="bg-brema-bg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="potenziale">🟢 Potenziale Rivenditore (solo catalogo)</SelectItem>
                  <SelectItem value="ufficiale">🔴 Rivenditore Ufficiale (B2B sbloccato)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label className="text-xs">Ragione sociale</Label><Input value={fiscal.company_name} onChange={setF("company_name")} data-testid="rf-company" className="bg-brema-bg" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Partita IVA</Label><Input value={fiscal.vat_number} onChange={setF("vat_number")} data-testid="rf-vat" className="bg-brema-bg" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Insegna negozio</Label><Input value={fiscal.shop_name} onChange={setF("shop_name")} data-testid="rf-shop" className="bg-brema-bg" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Referente</Label><Input value={fiscal.name} onChange={setF("name")} data-testid="rf-name" className="bg-brema-bg" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Telefono</Label><Input value={fiscal.phone} onChange={setF("phone")} data-testid="rf-phone" className="bg-brema-bg" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Email di contatto</Label><Input value={fiscal.contact_email} onChange={setF("contact_email")} data-testid="rf-contact-email" className="bg-brema-bg" /></div>
            </div>

            <div>
              <p className="mb-2 text-sm font-semibold text-foreground">Indirizzo di fatturazione</p>
              <AddressFields value={billing} onChange={setBilling} showLabel={false} />
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-semibold text-foreground">Indirizzi di spedizione</p>
                <Button size="sm" onClick={addAddr} data-testid="rf-add-address"
                  className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90"><Plus className="mr-1 h-3.5 w-3.5" /> Aggiungi</Button>
              </div>
              <div className="space-y-3">
                {addrs.map((a, i) => (
                  <div key={i} data-testid="rf-address-item" className="rounded-lg border border-border bg-brema-bg p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-medium text-foreground">{a.label || a.city || `Indirizzo ${i + 1}`} {a.is_default && <span className="text-brand">· predefinito</span>}</span>
                      <div className="flex gap-1.5">
                        {!a.is_default && <Button size="sm" variant="secondary" onClick={() => defAddr(i)} data-testid="rf-default-address" className="h-7 rounded-full border border-border bg-transparent text-[11px] text-foreground hover:bg-brema-hover"><Star className="mr-1 h-3 w-3" /> Predefinito</Button>}
                        <Button size="sm" variant="danger" onClick={() => delAddr(i)} data-testid="rf-del-address" className="h-7 rounded-full text-[11px]"><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </div>
                    <AddressFields value={a} onChange={(v) => updAddr(i, v)} />
                  </div>
                ))}
                {addrs.length === 0 && <p className="text-xs text-muted-foreground">Nessun indirizzo di spedizione.</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} data-testid="save-reseller-btn"
              className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Salva anagrafica</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={activityOpen} onOpenChange={setActivityOpen}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto border-border bg-brema-card" data-testid="reseller-activity-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Attività rivenditore</DialogTitle>
            <DialogDescription>Ultimo accesso al portale, carrello in sospeso/abbandonato e storico ordini inviati.</DialogDescription>
          </DialogHeader>
          {activityLoading || !activity ? (
            <div className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
          ) : (
            <div className="space-y-5">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-border bg-brema-bg p-3">
                  <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground"><Clock className="h-3.5 w-3.5" /> Ultimo accesso</p>
                  <p className="mt-1 text-sm font-medium text-foreground" data-testid="activity-last-login">{activity.profile.last_login ? dmyhm(activity.profile.last_login) : "Mai effettuato l'accesso"}</p>
                </div>
                <div className="rounded-lg border border-border bg-brema-bg p-3">
                  <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground"><ShoppingCart className="h-3.5 w-3.5" /> Ordini totali</p>
                  <p className="mt-1 text-sm font-medium text-foreground">{activity.orders.length}</p>
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-semibold text-foreground">Carrello attuale</p>
                {activity.cart ? (
                  <div className="rounded-lg border border-border bg-brema-bg p-3" data-testid="activity-cart">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      {(() => { const s = cartState(activity.cart.updated_at); return s ? <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${s.cls}`}>{s.label}</span> : null; })()}
                      <span className="text-xs text-muted-foreground">Aggiornato {dmyhm(activity.cart.updated_at)}</span>
                      <span className="ml-auto text-sm font-semibold text-foreground">{activity.cart.units} pz · {eur(activity.cart.total_excl)} <span className="text-[10px] text-muted-foreground">IVA escl.</span></span>
                    </div>
                    <div className="space-y-1">
                      {activity.cart.items.map((it, i) => (
                        <div key={i} data-testid="activity-cart-item" className="flex items-center justify-between rounded bg-brema-card px-2.5 py-1.5 text-xs">
                          <span className="text-foreground">{it.name}{it.size ? ` · ${it.size}` : ""}{it.color ? ` · ${it.color}` : ""}</span>
                          <span className="text-muted-foreground">{it.quantity} × {eur(it.unit_excl)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : <p className="rounded-lg border border-dashed border-border p-4 text-center text-xs text-muted-foreground">Nessun carrello in sospeso.</p>}
              </div>

              <div>
                <p className="mb-2 text-sm font-semibold text-foreground">Storico ordini</p>
                {activity.orders.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-border p-4 text-center text-xs text-muted-foreground">Nessun ordine inviato.</p>
                ) : (
                  <div className="overflow-hidden rounded-lg border border-border" data-testid="activity-orders">
                    <table className="w-full text-sm">
                      <thead className="border-b border-border bg-brema-bg text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr><th className="p-2.5">Data</th><th className="p-2.5">ID</th><th className="p-2.5">Stato</th><th className="p-2.5 text-right">Pezzi</th><th className="p-2.5 text-right">Totale</th></tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {activity.orders.map((o) => (
                          <tr key={o.id} data-testid="activity-order-row" className="text-foreground">
                            <td className="p-2.5 text-muted-foreground">{dmy(o.created_at)}</td>
                            <td className="p-2.5 font-mono text-xs text-muted-foreground">#{o.id.slice(-6).toUpperCase()}</td>
                            <td className="p-2.5"><OrderStatusBadge status={o.status} /></td>
                            <td className="p-2.5 text-right">{o.units}</td>
                            <td className="p-2.5 text-right font-semibold">{eur(o.total_incl_vat)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ShippingTab() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/shipping-config").then(({ data }) => setConfig(data.config)).catch(() => {});
  }, []);

  const updateZone = (idx, key, val) =>
    setConfig((c) => ({ ...c, zones: c.zones.map((z, i) => (i === idx ? { ...z, [key]: val } : z)) }));

  const save = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const payload = {
        zones: config.zones.map((z) => ({
          key: z.key, label: z.label,
          free_threshold: Number(z.free_threshold) || 0,
          flat_cost: Number(z.flat_cost) || 0,
        })),
        vat_rate: Number(config.vat_rate) || 22,
      };
      const { data } = await api.put("/shipping-config", payload);
      setConfig(data.config);
      toast.success("Regole di spedizione salvate");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio fallito");
    } finally { setSaving(false); }
  };

  if (!config) {
    return <div className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;
  }

  return (
    <div className="max-w-3xl space-y-5" data-testid="shipping-config">
      <div className="rounded-xl border border-border bg-brema-card p-5">
        <div className="mb-1 flex items-center gap-2 text-foreground">
          <Truck className="h-5 w-5 text-brand" />
          <h3 className="font-semibold">Regole di spedizione</h3>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">
          Imposta la soglia di spedizione gratuita (imponibile, IVA esclusa) e il costo fisso per gli ordini sotto soglia, per ogni zona di destinazione. Il carrello B2B calcola il trasporto in automatico in base alla nazione dell'indirizzo di spedizione.
        </p>
        <div className="space-y-4">
          {config.zones.map((z, idx) => (
            <div key={z.key} className="rounded-lg border border-border bg-brema-bg p-4" data-testid={`shipping-zone-${z.key}`}>
              <p className="mb-3 font-medium text-foreground">{z.label}</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Soglia spedizione gratuita (€, IVA escl.)</Label>
                  <Input type="number" min={0} step="0.01" value={z.free_threshold}
                    onChange={(e) => updateZone(idx, "free_threshold", e.target.value)}
                    data-testid={`ship-threshold-${z.key}`} className="h-9 bg-brema-card" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Costo trasporto sotto soglia (€, IVA escl.)</Label>
                  <Input type="number" min={0} step="0.01" value={z.flat_cost}
                    onChange={(e) => updateZone(idx, "flat_cost", e.target.value)}
                    data-testid={`ship-cost-${z.key}`} className="h-9 bg-brema-card" />
                </div>
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">
                Ordini ≥ {eur(z.free_threshold)} → <span className="text-green-600 dark:text-green-400">gratuita</span> · Ordini &lt; {eur(z.free_threshold)} → {eur(z.flat_cost)}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">IVA su trasporto (%)</Label>
            <Input type="number" min={0} step="0.1" value={config.vat_rate}
              onChange={(e) => setConfig((c) => ({ ...c, vat_rate: e.target.value }))}
              data-testid="ship-vat-rate" className="h-9 w-32 bg-brema-bg" />
          </div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-shipping-config"
          className="mt-5 rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Salva regole</>}
        </Button>
        <div className="mt-4 flex items-center gap-2 border-t border-border pt-4">
          <NotifyResellersButton category="spedizioni" testId="notify-shipping-btn" />
          <span className="text-xs text-muted-foreground">Dopo aver salvato, avvisa i Rivenditori Ufficiali della modifica.</span>
        </div>
      </div>
    </div>
  );
}

function ViesBadge({ reseller }) {
  const [status, setStatus] = useState(reseller.vies_status || null);
  const [name, setName] = useState(reseller.vies_name || "");
  const [reason, setReason] = useState(reseller.vies_reason || "");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (status) return;
    setLoading(true);
    api.post(`/resellers/${reseller.id}/verify-vat`)
      .then(({ data }) => { setStatus(data.status); setName(data.name); setReason(data.reason); })
      .catch(() => { setStatus("yellow"); setReason("Verifica non riuscita"); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);
  if (loading || !status) {
    return (
      <span data-testid="vies-badge" className="inline-flex items-center gap-1 rounded-full bg-brema-bg px-2 py-0.5 text-[10px] text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Verifica P.IVA…
      </span>
    );
  }
  const green = status === "green";
  return (
    <span data-testid="vies-badge" title={[reason, name && `VIES: ${name}`].filter(Boolean).join(" · ")}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${green ? "bg-positive/15 text-positive" : "bg-amber-500/15 text-amber-600 dark:text-amber-400"}`}>
      <ShieldCheck className="h-3 w-3" /> {green ? "P.IVA Valida" : "Da verificare"}
    </span>
  );
}

function RequestsTab({ onCount }) {
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const load = () => {
    setLoading(true);
    api.get("/resellers/pending")
      .then(({ data }) => { setReqs(data.resellers || []); onCount?.(data.count || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const approve = async (r) => {
    setBusy(r.id);
    try {
      const { data } = await api.post(`/resellers/${r.id}/approve`);
      toast.success(data.email_sent ? "Rivenditore approvato · email di benvenuto inviata" : "Rivenditore approvato e abilitato B2B");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Approvazione fallita"); }
    finally { setBusy(null); }
  };
  const reject = async (r) => {
    setBusy(r.id);
    try { await api.post(`/resellers/${r.id}/reject`); toast.success("Richiesta eliminata"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Eliminazione fallita"); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;
  if (!reqs.length) return <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-12 text-center text-muted-foreground" data-testid="requests-empty">Nessuna nuova richiesta di registrazione in attesa.</div>;

  return (
    <div className="space-y-3" data-testid="requests-list">
      {reqs.map((r) => {
        const addr = r.billing_address || {};
        const addrLine = [addr.address, [addr.zip, addr.city].filter(Boolean).join(" "), addr.province, addr.country].filter(Boolean).join(", ");
        const site = r.website ? (/^https?:\/\//.test(r.website) ? r.website : `https://${r.website}`) : "";
        return (
        <div key={r.id} data-testid="request-card" className="rounded-xl border border-brema-red/40 bg-brema-card p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 text-foreground">
                <Building2 className="h-4 w-4 shrink-0 text-brand" />
                <span className="font-semibold">{r.company_name || r.shop_name || r.name}</span>
                <span className="rounded-full bg-brema-red/15 px-2 py-0.5 text-[10px] font-semibold text-brema-red">NUOVO</span>
                <span className="rounded-full bg-positive/10 px-2 py-0.5 text-[10px] font-semibold text-positive">Potenziale</span>
                <ViesBadge reseller={r} />
              </div>
              <div className="mt-2 grid gap-x-6 gap-y-1 text-xs text-muted-foreground sm:grid-cols-2" data-testid="request-details">
                <span>P.IVA / VIES: <span className="text-foreground">{r.vat_number || "—"}</span></span>
                <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {r.contact_email || r.email}</span>
                <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {r.phone || "—"}</span>
                {r.name && <span>Referente: <span className="text-foreground">{r.name}</span></span>}
                <span className="flex items-start gap-1 sm:col-span-2"><MapPin className="mt-0.5 h-3 w-3 shrink-0" /> {addrLine || "Indirizzo non fornito"}</span>
                {site && <span className="flex items-center gap-1 sm:col-span-2"><Globe className="h-3 w-3 shrink-0" /> <a href={site} target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">{r.website}</a></span>}
              </div>
            </div>
            <span className="shrink-0 text-xs text-muted-foreground">{r.created_at ? dmyhm(r.created_at) : ""}</span>
          </div>
          {(r.note || r.contact_message) && (
            <p className="mt-3 whitespace-pre-wrap rounded-lg bg-brema-bg p-3 text-sm text-foreground" data-testid="request-note">{r.note || r.contact_message}</p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" data-testid="approve-reseller-btn" disabled={busy === r.id} onClick={() => approve(r)}
              className="rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">
              {busy === r.id ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />} Approva e Abilita B2B
            </Button>
            <a href={`mailto:${r.contact_email || r.email}?subject=${encodeURIComponent("Brema Bike — richiesta rivenditore")}`} data-testid="request-reply-btn">
              <Button size="sm" variant="secondary" className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover"><Mail className="mr-1.5 h-3.5 w-3.5" /> Rispondi</Button>
            </a>
            <Button size="sm" variant="danger" data-testid="request-delete-btn" disabled={busy === r.id} onClick={() => reject(r)}
              className="rounded-full text-xs"><Trash2 className="mr-1.5 h-3.5 w-3.5" /> Elimina</Button>
          </div>
        </div>
        );
      })}
    </div>
  );
}

function SalesConditionsTab() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/sales-conditions").then(({ data }) => setConfig(data.config)).catch(() => {});
  }, []);

  const updateTier = (zi, ti, key, val) =>
    setConfig((c) => ({ ...c, zones: c.zones.map((z, i) => (i === zi ? {
      ...z, tiers: z.tiers.map((t, j) => (j === ti ? { ...t, [key]: val } : t)),
    } : z)) }));
  const addTier = (zi) =>
    setConfig((c) => ({ ...c, zones: c.zones.map((z, i) => (i === zi ? {
      ...z, tiers: [...z.tiers, { min: 0, max: null, discount: "0" }],
    } : z)) }));
  const removeTier = (zi, ti) =>
    setConfig((c) => ({ ...c, zones: c.zones.map((z, i) => (i === zi ? {
      ...z, tiers: z.tiers.filter((_, j) => j !== ti),
    } : z)) }));

  const save = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const payload = { zones: config.zones.map((z) => ({
        key: z.key, label: z.label,
        tiers: z.tiers.map((t) => ({
          min: Number(t.min) || 0,
          max: (t.max === null || t.max === "" || t.max === undefined) ? null : Number(t.max),
          discount: String(t.discount ?? "0"),
        })),
      })) };
      const { data } = await api.put("/sales-conditions", payload);
      setConfig(data.config);
      toast.success("Condizioni di vendita salvate");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio fallito");
    } finally { setSaving(false); }
  };

  if (!config) {
    return <div className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;
  }

  return (
    <div className="max-w-3xl space-y-5" data-testid="sales-conditions-config">
      <div className="rounded-xl border border-border bg-brema-card p-5">
        <div className="mb-1 flex items-center gap-2 text-foreground">
          <Percent className="h-5 w-5 text-brand" />
          <h3 className="font-semibold">Condizioni di vendita — sconti a scaglioni</h3>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">
          Imposta gli scaglioni di sconto sull'imponibile dell'ordine (IVA esclusa) per area geografica. Il carrello B2B applica automaticamente la percentuale in base alla nazione di destinazione e all'importo. Puoi scrivere una percentuale singola (es. <b>40</b>) oppure composta/cascata (es. <b>40+5</b> → prima 40%, poi 5% sul residuo).
        </p>
        <div className="space-y-5">
          {config.zones.map((z, zi) => { const warns = validateTiers(z.tiers); return (
            <div key={z.key} className="rounded-lg border border-border bg-brema-bg p-4" data-testid={`sales-zone-${z.key}`}>
              <p className="mb-3 font-medium text-foreground">{z.label}</p>
              <div className="mb-1 grid grid-cols-[1fr_1fr_1fr_auto] gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                <span>Da (€, imponibile)</span><span>A (€, vuoto = oltre)</span><span>Sconto %</span><span />
              </div>
              <div className="space-y-3">
                {z.tiers.map((t, ti) => {
                  const eff = effectivePct(parseDiscountExpr(t.discount));
                  return (
                    <div key={ti} data-testid={`sales-tier-${z.key}-${ti}`}>
                      <div className="grid grid-cols-[1fr_1fr_1fr_auto] items-center gap-2">
                        <Input type="number" min={0} step="0.01" value={t.min ?? 0}
                          onChange={(e) => updateTier(zi, ti, "min", e.target.value)}
                          data-testid={`tier-min-${z.key}-${ti}`} className="h-9 bg-brema-card" />
                        <Input type="number" min={0} step="0.01" value={t.max ?? ""} placeholder="oltre"
                          onChange={(e) => updateTier(zi, ti, "max", e.target.value)}
                          data-testid={`tier-max-${z.key}-${ti}`} className="h-9 bg-brema-card" />
                        <Input value={t.discount ?? ""} placeholder="es. 40 o 40+5"
                          onChange={(e) => updateTier(zi, ti, "discount", e.target.value)}
                          data-testid={`tier-discount-${z.key}-${ti}`} className="h-9 bg-brema-card" />
                        <Button variant="danger" size="sm" onClick={() => removeTier(zi, ti)}
                          data-testid={`tier-remove-${z.key}-${ti}`} className="h-9 px-2">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        Sconto effettivo applicato: <span className="font-semibold text-brand" data-testid={`tier-eff-${z.key}-${ti}`}>{eff}%</span>
                      </p>
                    </div>
                  );
                })}
              </div>
              {warns.length > 0 && (
                <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3" data-testid={`sales-gap-warning-${z.key}`}>
                  <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-amber-600 dark:text-amber-300">
                    <AlertTriangle className="h-3.5 w-3.5" /> Fasce non contigue
                  </div>
                  <ul className="list-disc space-y-0.5 pl-5 text-[11px] text-amber-700 dark:text-amber-200">
                    {warns.map((w, wi) => <li key={wi}>{w}</li>)}
                  </ul>
                </div>
              )}
              <Button variant="secondary" size="sm" onClick={() => addTier(zi)}
                data-testid={`tier-add-${z.key}`} className="mt-3 rounded-full border border-border bg-transparent text-xs">
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Aggiungi fascia
              </Button>
            </div>
          ); })}
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-sales-conditions"
          className="mt-5 rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Salva condizioni</>}
        </Button>
        <div className="mt-4 flex items-center gap-2 border-t border-border pt-4">
          <NotifyResellersButton category="sconti" testId="notify-sales-btn" />
          <ResellerAlertsHistory />
          <span className="text-xs text-muted-foreground">Dopo aver salvato, avvisa i Rivenditori Ufficiali della modifica.</span>
        </div>
      </div>
    </div>
  );
}

function VolumeDiscountReportTab() {
  const fmt = (d) => d.toISOString().slice(0, 10);
  const [from, setFrom] = useState(fmt(new Date(Date.now() - 90 * 864e5)));
  const [to, setTo] = useState(fmt(new Date()));
  const [zone, setZone] = useState("tutte");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = { from_date: from, to_date: to };
    if (zone !== "tutte") params.zone = zone;
    api.get("/reports/volume-discounts", { params })
      .then(({ data }) => setReport(data))
      .catch(() => toast.error("Caricamento report fallito"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const rows = report ? [
    { key: "europa", ...report.zones.europa },
    { key: "extra_ue", ...report.zones.extra_ue },
  ].filter((r) => zone === "tutte" || r.key === zone) : [];

  return (
    <div className="max-w-4xl space-y-5" data-testid="volume-discount-report">
      <div className="rounded-xl border border-border bg-brema-card p-5">
        <div className="mb-1 flex items-center gap-2 text-foreground">
          <BarChart3 className="h-5 w-5 text-brand" />
          <h3 className="font-semibold">Report sconti volume</h3>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">
          Riepilogo degli sconti volume concessi sugli ordini B2B per periodo e area geografica.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1"><Label className="text-xs text-muted-foreground">Dal</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from" className="h-9 w-[160px] bg-brema-bg" /></div>
          <div className="space-y-1"><Label className="text-xs text-muted-foreground">Al</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to" className="h-9 w-[160px] bg-brema-bg" /></div>
          <div className="space-y-1"><Label className="text-xs text-muted-foreground">Area</Label>
            <Select value={zone} onValueChange={setZone}>
              <SelectTrigger data-testid="report-zone" className="h-9 w-[220px] bg-brema-bg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutte">Tutte le aree</SelectItem>
                <SelectItem value="europa">Europa (Italia inclusa)</SelectItem>
                <SelectItem value="extra_ue">Oltreoceano / Extra UE</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={load} disabled={loading} data-testid="report-apply"
            className="h-9 rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aggiorna"}
          </Button>
        </div>
      </div>

      {report && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-border bg-brema-card p-4" data-testid="report-total-orders">
              <p className="text-xs text-muted-foreground">Ordini nel periodo</p>
              <p className="mt-1 text-2xl font-bold text-foreground">{report.total.orders}</p>
            </div>
            <div className="rounded-xl border border-border bg-brema-card p-4" data-testid="report-total-discount">
              <p className="text-xs text-muted-foreground">Sconti volume concessi</p>
              <p className="mt-1 text-2xl font-bold text-brand">{eur(report.total.discount_amount)}</p>
            </div>
            <div className="rounded-xl border border-border bg-brema-card p-4" data-testid="report-avg-pct">
              <p className="text-xs text-muted-foreground">Sconto medio</p>
              <p className="mt-1 text-2xl font-bold text-foreground">{report.total.avg_discount_pct}%</p>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-border bg-brema-card">
            <table className="w-full text-sm" data-testid="report-table">
              <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="p-3">Area</th>
                  <th className="p-3 text-right">Ordini</th>
                  <th className="p-3 text-right">Imponibile listino</th>
                  <th className="p-3 text-right">Sconti concessi</th>
                  <th className="p-3 text-right">Netto post-sconto</th>
                  <th className="p-3 text-right">Sconto medio</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} data-testid={`report-row-${r.key}`} className="border-b border-border text-foreground last:border-0">
                    <td className="p-3 font-medium">{r.label}</td>
                    <td className="p-3 text-right">{r.orders}</td>
                    <td className="p-3 text-right">{eur(r.list_subtotal)}</td>
                    <td className="p-3 text-right text-brand">{eur(r.discount_amount)}</td>
                    <td className="p-3 text-right">{eur(r.net_subtotal)}</td>
                    <td className="p-3 text-right">{r.avg_discount_pct}%</td>
                  </tr>
                ))}
                {zone === "tutte" && (
                  <tr data-testid="report-row-total" className="bg-brema-bg font-semibold text-foreground">
                    <td className="p-3">Totale</td>
                    <td className="p-3 text-right">{report.total.orders}</td>
                    <td className="p-3 text-right">{eur(report.total.list_subtotal)}</td>
                    <td className="p-3 text-right text-brand">{eur(report.total.discount_amount)}</td>
                    <td className="p-3 text-right">{eur(report.total.net_subtotal)}</td>
                    <td className="p-3 text-right">{report.total.avg_discount_pct}%</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}


export default function OrdersAdmin() {
  const [pendingCount, setPendingCount] = useState(0);
  const [msgCount, setMsgCount] = useState(0);
  useEffect(() => {
    api.get("/resellers/pending").then(({ data }) => setPendingCount(data.count || 0)).catch(() => {});
    api.get("/contact-requests").then(({ data }) => setMsgCount(data.unread || 0)).catch(() => {});
  }, []);
  return (
    <div>
      <PageHeader eyebrow="Plancia di comando Brand" title="Ordini & Rivenditori"
        subtitle="Consulta gli ordini B2B, aggiorna lo stato di evasione e gestisci l'anagrafica dei rivenditori." />
      <Tabs defaultValue="orders">
        <TabsList className="flex-wrap bg-brema-card">
          <TabsTrigger value="orders" data-testid="tab-admin-orders"><ShoppingCart className="mr-1.5 h-4 w-4" /> Ordini</TabsTrigger>
          <TabsTrigger value="resellers" data-testid="tab-admin-resellers"><Users className="mr-1.5 h-4 w-4" /> Rivenditori</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-admin-requests">
            <Mail className="mr-1.5 h-4 w-4" /> Richieste
            {pendingCount > 0 && (
              <span data-testid="requests-badge" className="ml-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brema-red px-1 text-[10px] font-bold leading-none text-white">{pendingCount}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-admin-messages">
            <MessageSquare className="mr-1.5 h-4 w-4" /> Messaggi
            {msgCount > 0 && (
              <span data-testid="messages-badge" className="ml-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brema-red px-1 text-[10px] font-bold leading-none text-white">{msgCount}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="shipping" data-testid="tab-admin-shipping"><Truck className="mr-1.5 h-4 w-4" /> Spedizioni</TabsTrigger>
          <TabsTrigger value="sales-conditions" data-testid="tab-admin-sales-conditions"><Percent className="mr-1.5 h-4 w-4" /> Condizioni di vendita</TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-admin-report"><BarChart3 className="mr-1.5 h-4 w-4" /> Report sconti</TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-admin-stats"><Activity className="mr-1.5 h-4 w-4" /> Statistiche & Report</TabsTrigger>
        </TabsList>
        <TabsContent value="orders"><AdminOrdersTab /></TabsContent>
        <TabsContent value="resellers"><ResellersTab /></TabsContent>
        <TabsContent value="requests"><RequestsTab onCount={setPendingCount} /></TabsContent>
        <TabsContent value="messages"><ContactMessagesTab onCount={setMsgCount} /></TabsContent>
        <TabsContent value="shipping"><ShippingTab /></TabsContent>
        <TabsContent value="sales-conditions"><SalesConditionsTab /></TabsContent>
        <TabsContent value="report"><VolumeDiscountReportTab /></TabsContent>
        <TabsContent value="stats"><B2BStatsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
